import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Search, TrendingDown, TrendingUp, Plus, Trash2, BarChart2, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteAnalysisPage = ({ user }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    part_name: '', part_number: '', vendor_name: '', vendor_contact: '',
    vendor_email: '', unit_price: 0, gst_percentage: 18, quantity: 1,
    quote_date: new Date().toISOString().split('T')[0], notes: ''
  });
  const [saving, setSaving] = useState(false);

  // "Use This Quote" state
  const [useQuoteOpen, setUseQuoteOpen] = useState(false);
  const [useQuoteForm, setUseQuoteForm] = useState({});
  const [approvedPRs, setApprovedPRs] = useState([]);
  const [selectedPR, setSelectedPR] = useState('');
  const [applyingQuote, setApplyingQuote] = useState(false);
  const inputRef = useRef(null);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim() || query.trim().length < 2) {
      toast.error('Please enter at least 2 characters to search');
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/vendor-quotes/search?q=${encodeURIComponent(query.trim())}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuote = async () => {
    if (!addForm.part_name || !addForm.vendor_name || !addForm.unit_price) {
      toast.error('Part name, vendor name, and unit price are required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/vendor-quotes`, addForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote saved successfully');
      setAddOpen(false);
      setAddForm({
        part_name: '', part_number: '', vendor_name: '', vendor_contact: '',
        vendor_email: '', unit_price: 0, gst_percentage: 18, quantity: 1,
        quote_date: new Date().toISOString().split('T')[0], notes: ''
      });
      if (searched && query.trim().length >= 2) handleSearch();
    } catch {
      toast.error('Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuote = async (quoteId, partKey) => {
    if (!window.confirm('Remove this quote?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/vendor-quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote removed');
      // Refresh results
      if (searched) handleSearch();
    } catch { toast.error('Failed to remove quote'); }
  };

  const openUseQuote = async (q) => {
    setUseQuoteForm({
      part_name: q.part_name,
      part_number: q.part_number || '',
      vendor_name: q.vendor_name,
      vendor_contact: q.vendor_contact || '',
      vendor_email: q.vendor_email || '',
      actual_unit_price: q.unit_price,
      gst_percentage: q.gst_percentage || 18,
      quantity_ordered: q.quantity || 1,
      payment_terms: '',
      notes: q.notes || '',
    });
    setSelectedPR('');
    setUseQuoteOpen(true);
    // Fetch approved PRs
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/purchase-requisitions?status=approved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApprovedPRs(res.data || []);
    } catch { setApprovedPRs([]); }
  };

  const handleApplyQuote = async () => {
    if (!selectedPR) { toast.error('Please select a PR to apply this quote to'); return; }
    if (!useQuoteForm.actual_unit_price) { toast.error('Unit price is required'); return; }
    setApplyingQuote(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        actual_unit_price: useQuoteForm.actual_unit_price,
        vendor_name: useQuoteForm.vendor_name,
        quantity_ordered: useQuoteForm.quantity_ordered,
      });
      if (useQuoteForm.vendor_contact) params.set('vendor_contact', useQuoteForm.vendor_contact);
      if (useQuoteForm.vendor_email) params.set('vendor_email', useQuoteForm.vendor_email);
      if (useQuoteForm.gst_percentage) params.set('gst_percentage', useQuoteForm.gst_percentage);
      if (useQuoteForm.payment_terms) params.set('payment_terms', useQuoteForm.payment_terms);
      if (useQuoteForm.notes) params.set('notes', useQuoteForm.notes);
      await axios.put(`${API}/purchase-requisitions/${selectedPR}/order?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pr = approvedPRs.find(p => p.id === selectedPR);
      toast.success(`Quote applied! PR ${pr?.pr_number || ''} is now marked as ordered.`);
      setUseQuoteOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply quote');
    } finally { setApplyingQuote(false); }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Quote Analysis</h1>
          <p className="text-slate-600 mt-1">Search historical vendor quotes by part number or name</p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="add-quote-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Quote
        </Button>
      </div>

      {/* Search Box */}
      <form onSubmit={handleSearch} className="bg-white rounded-xl border shadow-sm p-5">
        <Label className="text-sm font-medium text-slate-700 mb-2 block">
          Search by Part Number or Part Name
        </Label>
        <div className="flex gap-3">
          <Input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="e.g. HYD-PUMP-001 or Hydraulic Pump..."
            className="flex-1 h-10"
            data-testid="quote-search-input"
          />
          <Button type="submit" disabled={loading} className="bg-slate-900 hover:bg-slate-800 px-6" data-testid="quote-search-btn">
            <Search className="h-4 w-4 mr-2" />
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </div>
      </form>

      {/* Results */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900" />
        </div>
      )}

      {!loading && searched && results.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border">
          <BarChart2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No quotes found</p>
          <p className="text-slate-400 text-sm mt-1">Try a different part number or name</p>
        </div>
      )}

      {!loading && results.map((part, pi) => {
        const { min_price, max_price } = part;
        return (
          <div key={pi} className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid={`part-result-${pi}`}>
            {/* Part Header */}
            <div className="px-5 py-4 bg-slate-50 border-b flex items-center justify-between">
              <div>
                <p className="font-bold text-slate-900 text-base">{part.part_name}</p>
                {part.part_number && (
                  <p className="text-xs text-slate-500 font-mono mt-0.5">#{part.part_number}</p>
                )}
              </div>
              <div className="flex gap-6 text-center">
                <div>
                  <p className="text-xs text-slate-500">Quotes</p>
                  <p className="text-lg font-bold text-slate-900">{part.quote_count}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Best Price</p>
                  <p className="text-lg font-bold text-green-700 flex items-center gap-1">
                    <TrendingDown className="h-4 w-4" />
                    ₹{min_price.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Highest</p>
                  <p className="text-lg font-bold text-red-600 flex items-center gap-1">
                    <TrendingUp className="h-4 w-4" />
                    ₹{max_price.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Average</p>
                  <p className="text-lg font-bold text-slate-700">₹{part.avg_price.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Quotes Table */}
            <Table>
              <TableHeader>
                <TableRow className="bg-white">
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">GST %</TableHead>
                  <TableHead className="text-right">Total (incl. GST)</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Added By</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {part.quotes.map((q) => {
                  const isLowest = q.unit_price === min_price;
                  const isHighest = q.unit_price === max_price && min_price !== max_price;
                  return (
                    <TableRow
                      key={q.id}
                      className={isLowest ? 'bg-green-50' : isHighest ? 'bg-red-50' : ''}
                      data-testid={`quote-row-${q.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="font-medium text-sm">{q.vendor_name}</p>
                            {q.vendor_contact && <p className="text-xs text-slate-400">{q.vendor_contact}</p>}
                          </div>
                          {isLowest && (
                            <Badge className="bg-green-100 text-green-700 border border-green-300 text-xs" data-testid={`lowest-badge-${q.id}`}>
                              Lowest
                            </Badge>
                          )}
                          {isHighest && (
                            <Badge className="bg-red-100 text-red-700 border border-red-300 text-xs" data-testid={`highest-badge-${q.id}`}>
                              Highest
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={`text-right font-semibold ${isLowest ? 'text-green-700' : isHighest ? 'text-red-600' : ''}`}>
                        ₹{q.unit_price.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-sm">{q.gst_percentage}%</TableCell>
                      <TableCell className="text-right font-semibold">₹{(q.total_price || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-slate-600">{q.quote_date}</TableCell>
                      <TableCell className="text-sm text-slate-500 max-w-xs truncate">{q.notes || '—'}</TableCell>
                      <TableCell className="text-xs text-slate-400">{q.created_by}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 border-indigo-200 text-xs"
                            onClick={() => openUseQuote(q)}
                            data-testid={`use-quote-${q.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Use
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteQuote(q.id)}
                            data-testid={`delete-quote-${q.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        );
      })}

      {/* Use This Quote Dialog */}
      <Dialog open={useQuoteOpen} onOpenChange={setUseQuoteOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Use This Quote — Apply to PR</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {/* Part Info */}
            <div className="bg-slate-50 rounded-lg px-4 py-3">
              <p className="text-xs text-slate-500">Part</p>
              <p className="font-semibold text-slate-900">{useQuoteForm.part_name}</p>
              {useQuoteForm.part_number && <p className="text-xs font-mono text-slate-500">#{useQuoteForm.part_number}</p>}
            </div>

            {/* Select PR */}
            <div>
              <Label className="text-xs font-medium">Apply to PR (Approved Indents) *</Label>
              <Select value={selectedPR} onValueChange={setSelectedPR}>
                <SelectTrigger className="mt-1 h-9" data-testid="use-quote-pr-select">
                  <SelectValue placeholder={approvedPRs.length === 0 ? 'No approved PRs available' : 'Select a PR...'} />
                </SelectTrigger>
                <SelectContent>
                  {approvedPRs.map(pr => (
                    <SelectItem key={pr.id} value={pr.id}>
                      {pr.pr_number} — {pr.part_name} {pr.door_no ? `(${pr.door_no})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {approvedPRs.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No approved PRs found. Approve an indent first.</p>
              )}
            </div>

            {/* Editable fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Vendor Name *</Label>
                <Input className="mt-1 h-8" value={useQuoteForm.vendor_name || ''}
                  onChange={e => setUseQuoteForm(f => ({ ...f, vendor_name: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Contact</Label>
                <Input className="mt-1 h-8" value={useQuoteForm.vendor_contact || ''}
                  onChange={e => setUseQuoteForm(f => ({ ...f, vendor_contact: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input className="mt-1 h-8" value={useQuoteForm.vendor_email || ''}
                  onChange={e => setUseQuoteForm(f => ({ ...f, vendor_email: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Unit Price (₹) *</Label>
                <Input className="mt-1 h-8" type="number" min="0"
                  value={useQuoteForm.actual_unit_price || 0}
                  onChange={e => setUseQuoteForm(f => ({ ...f, actual_unit_price: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label className="text-xs">GST %</Label>
                <Input className="mt-1 h-8" type="number" min="0"
                  value={useQuoteForm.gst_percentage || 18}
                  onChange={e => setUseQuoteForm(f => ({ ...f, gst_percentage: parseFloat(e.target.value) || 18 }))} />
              </div>
              <div>
                <Label className="text-xs">Qty Ordered</Label>
                <Input className="mt-1 h-8" type="number" min="1"
                  value={useQuoteForm.quantity_ordered || 1}
                  onChange={e => setUseQuoteForm(f => ({ ...f, quantity_ordered: parseInt(e.target.value) || 1 }))} />
              </div>
              <div>
                <Label className="text-xs">Payment Terms</Label>
                <Input className="mt-1 h-8" placeholder="e.g. 30 days"
                  value={useQuoteForm.payment_terms || ''}
                  onChange={e => setUseQuoteForm(f => ({ ...f, payment_terms: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notes</Label>
                <Input className="mt-1 h-8" value={useQuoteForm.notes || ''}
                  onChange={e => setUseQuoteForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setUseQuoteOpen(false)}>Cancel</Button>
              <Button
                className="bg-indigo-700 hover:bg-indigo-800"
                onClick={handleApplyQuote}
                disabled={applyingQuote || !selectedPR}
                data-testid="apply-quote-btn"
              >
                {applyingQuote ? 'Applying...' : 'Apply & Mark as Ordered'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Quote Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Vendor Quote</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Part Name *</Label>
                <Input className="mt-1 h-8" value={addForm.part_name}
                  onChange={e => setAddForm(f => ({ ...f, part_name: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Part Number</Label>
                <Input className="mt-1 h-8" value={addForm.part_number}
                  onChange={e => setAddForm(f => ({ ...f, part_number: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Vendor Name *</Label>
                <Input className="mt-1 h-8" value={addForm.vendor_name}
                  onChange={e => setAddForm(f => ({ ...f, vendor_name: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Contact</Label>
                <Input className="mt-1 h-8" value={addForm.vendor_contact}
                  onChange={e => setAddForm(f => ({ ...f, vendor_contact: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input className="mt-1 h-8" type="email" value={addForm.vendor_email}
                  onChange={e => setAddForm(f => ({ ...f, vendor_email: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Unit Price (₹) *</Label>
                <Input className="mt-1 h-8" type="number" min="0" value={addForm.unit_price}
                  onChange={e => setAddForm(f => ({ ...f, unit_price: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label className="text-xs">GST %</Label>
                <Input className="mt-1 h-8" type="number" min="0" value={addForm.gst_percentage}
                  onChange={e => setAddForm(f => ({ ...f, gst_percentage: parseFloat(e.target.value) || 18 }))} />
              </div>
              <div>
                <Label className="text-xs">Quantity</Label>
                <Input className="mt-1 h-8" type="number" min="1" value={addForm.quantity}
                  onChange={e => setAddForm(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))} />
              </div>
              <div>
                <Label className="text-xs">Quote Date</Label>
                <Input className="mt-1 h-8" type="date" value={addForm.quote_date}
                  onChange={e => setAddForm(f => ({ ...f, quote_date: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notes</Label>
                <Input className="mt-1 h-8" value={addForm.notes}
                  onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Valid until, conditions, etc." />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleAddQuote} disabled={saving}
                data-testid="save-quote-btn">
                {saving ? 'Saving...' : 'Save Quote'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuoteAnalysisPage;
